import { PAYMENT_MODES } from './constants';

// Revenue section as two rolling subtractions, shared by Reports, Range Reports
// and both exports so every surface prints the same rows in the same order.
//
//   Block 1  Gross Revenue → Net Collection
//   Block 2  Net Collection → Cash in Hand
//
// Row: { label, sign: '' | '+' | '−', amount (absolute), running, kind: 'start' | 'line' | 'result' }
// The running column is what a reader follows down the page; the result row IS
// the running figure, so each block closes by construction.
//
// Why Block 1 closes: receipts are recorded net of TAC at source, so
//   Received = Gross − TAC − Unpaid + Received on cancelled bookings
// and Net Collection = Received − Driver Commission − Refunds ± Adjustments.
// Cancelled bookings sit outside Gross (they did not sail) but their money is
// real until refunded, hence the two conditional lines.
export function buildRevenueBlocks(r) {
  const block = (startLabel, start) => {
    let running = start;
    const rows = [{ label: startLabel, sign: '', amount: start, running, kind: 'start' }];
    return {
      rows,
      // `always` lines print even at zero — the fixed shape of the block; the
      // rest appear only when they carry a figure. `dir` is the line's sign
      // ('−' deduction / '+' addition); `magnitude` may be negative to flip it,
      // as with a signed adjustment.
      line: (label, dir, magnitude, always = false) => {
        if (!always && Math.abs(magnitude) < 0.005) return;
        const effect = dir === '−' ? -magnitude : magnitude;
        running += effect;
        const sign = effect === 0 ? dir : effect < 0 ? '−' : '+';
        rows.push({ label, sign, amount: Math.abs(effect), running, kind: 'line' });
      },
      result: (label) => { rows.push({ label, sign: '', amount: running, running, kind: 'result' }); return running; },
    };
  };

  const b1 = block('Gross Revenue', r.gross);
  b1.line('TAC at source', '−', r.totalTAC || 0, true);
  b1.line('Driver Commission', '−', r.driverCommission || 0, true);
  b1.line('Balance (unpaid)', '−', r.unpaidRemaining || 0, true);
  b1.line('Received on cancelled bookings', '+', r.cancelledReceipts || 0);
  b1.line('Refunds', '−', r.totalRefunds || 0);
  b1.line('Daily Cash Adjustment', '+', r.adjNet || 0);   // signed: negative = money out
  const netCollection = b1.result('Net Collection');

  const b2 = block('Net Collection', netCollection);
  PAYMENT_MODES.filter(m => m !== 'Cash').forEach(m => b2.line(`${m} Collection`, '−', r.byMode?.[m] || 0, true));
  // A refund whose mode is not one of the five was taken off Net Collection but
  // belongs to no mode column and did not leave the drawer — add it back here so
  // the block still lands on the drawer figure. Never appears with clean data.
  b2.line('Refunds with no recorded mode', '+', -(r.uncategorised || 0));
  const cashInHand = b2.result('Cash in Hand');

  const closes = Math.abs(netCollection - (r.netCollection || 0)) < 0.5 && Math.abs(cashInHand - (r.cashInHand || 0)) < 0.5;
  if (!closes) {
    // Should be impossible — the receipts-based figures and the block are the
    // same identity. Surface it rather than silently print two numbers.
    console.warn('Revenue blocks do not close', { netCollection, cashInHand, report: { net: r.netCollection, cash: r.cashInHand } });
  }
  return { block1: b1.rows, block2: b2.rows, closes };
}

// Rows as plain [label, amount, running] strings for the exports.
// `money` formats a number. The sign is carried on the amount column; the label
// stays plain (Excel gets the typographic minus, the PDF's Helvetica cannot
// render U+2212 so it takes a hyphen-minus via `minus`).
export function blockRowsForExport(rows, money, { minus = '−' } = {}) {
  const sign = (s) => (s === '−' ? minus : s);
  return rows.map(row => [
    row.label,
    row.kind === 'line' ? `${sign(row.sign)}${money(row.amount)}` : money(row.amount),
    row.kind === 'start' ? '' : money(row.running),
  ]);
}
